/**
 * types.ts — Shared type definitions for all data entities.
 * These are re-exported from mockData.ts for backward compat.
 */

export type GstTreatment = 'ex' | 'inc';
/** FieldInsight bill lifecycle — four states only. */
export type BillStatus =
  | 'Draft'
  | 'Awaiting approval'
  | 'Approved'
  | 'Paid';

export const BILL_STATUSES: BillStatus[] = [
  'Draft',
  'Awaiting approval',
  'Approved',
  'Paid',
];

/** FieldInsight PO lifecycle — eight states only. */
export type PoStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'Partial received'
  | 'All received'
  | 'Bill received'
  | 'Finalised'
  | 'Cancelled';

export const PO_STATUSES: PoStatus[] = [
  'Draft',
  'Submitted',
  'Approved',
  'Partial received',
  'All received',
  'Bill received',
  'Finalised',
  'Cancelled',
];

export interface Supplier {
  id: string;
  code: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  abn: string;
  terms: string;
  defaultMarkup: number;
}

export interface Product {
  id: number;
  code: string;
  description: string;
  category: string;
  primarySupplier: string;
  purchasePrice: number;
  markup: number;
  stock: number;
  minStock: number;
}

export interface PurchaseOrder {
  id: string;
  date: string;
  deliveryDate?: string;
  supplierId: string;
  jobRef: string;
  reference: string;
  notes?: string;
  lines: number;
  exGst: number;
  gst: number;
  total: number;
  status: PoStatus;

  // ── Edit-form fields (v1.12.0) — optional, surfaced by POEditForm ─────
  // Mirrors the FieldInsight PO authoring screen. All fields are optional
  // so existing seed data + POs created before v1.12.0 keep working.
  title?: string;                   // free-text title (defaults to reference)
  siteId?: string;                  // links to Site entity
  projectId?: string;               // links to Project entity
  invoiceTerm?: string;             // '14 Day Terms', '30 Days EOM', etc.
  deliveryStatus?: string;          // 'Pending', 'Partial', 'Delivered'
  deliveryDueDate?: string;         // YYYY-MM-DD
  deliveredDate?: string;           // YYYY-MM-DD
  supplierInvoiceNo?: string;
  supplierInvoiceDate?: string;     // YYYY-MM-DD
  paymentMethod?: string;           // 'EFT', 'Card', 'Cash'
  template?: string;                // 'Original Purchase Order'
  ownerSalesRep?: string;           // free-text owner / sales-rep name
  currency?: string;                // 'AUD - $'
  division?: string;
  sent?: boolean;
  labourHire?: boolean;
  internalNotes?: string;
  deliveryAddress?: string;         // free-text overrides Site address
  exclusionsInclusions?: string;    // 'Exclusions / Inclusions / Special Conditions'
  // v1.18 — straight file attachments (no parsing): just the file names that
  // have been attached to this PO. The AI Import flow is separate (it parses).
  attachments?: string[];
}

// ── Site entity (v1.12.0) ─────────────────────────────────────────────────────
// A FieldInsight Site is the customer-side delivery / job location.
// Used in the PO edit form's Site search picker. Independent of the
// supplier address (suppliers ship FROM, sites receive TO).
export interface Site {
  id: string;
  name: string;
  businessName?: string;
  email?: string;
  address: string;
}

// ── Project entity (v1.12.0) ─────────────────────────────────────────────────
// A grouping of jobs / POs under a single project banner. Used in the
// PO edit form's Project search picker.
export interface Project {
  id: string;
  name: string;
  number: string;              // e.g. 'PROJ-9'
  startDate?: string;          // YYYY-MM-DD
  endDate?: string;            // YYYY-MM-DD
}

/**
 * SAP-style item category, matching FieldInsight's PO Type column:
 *   product — stock item (even if not warehoused); wants an internal code.
 *   service — labour etc.; no supplier code, internal code OPTIONAL.
 */
export type POLineType = 'product' | 'service';

export interface POLineDetail {
  poId: string;
  inventoryCode: string;
  // v1.21 — service lines skip internal-code requirement (labour, freight, etc.)
  lineType?: POLineType;
  // v1.17 — supplier's own code for this line, kept DISTINCT from the internal
  // inventoryCode. Populated when a PO line is born from a parsed quote/bill so
  // the line can be reconciled (supplier code -> internal code) without ever
  // dumping the supplier code into the internal-code field. Transient context:
  // the persisted supplier->internal mapping lives in SupplierCodeMap, written
  // only at reconciliation.
  supplierCode?: string;
  description: string;
  orderedQty: number;
  /** Physical / expected qty on this PO line (legacy). Defaults to orderedQty when unset. */
  actualQty?: number;
  receivedToDate: number;
  /** FI “Unused qty” — received but not yet used on the job. */
  unusedQty?: number;
  unitPrice: number;
  discount: number;
  /** FI Category column — falls back to product.category when unset. */
  category?: string;
  /** FI Warehouse column. */
  warehouse?: string;
  /** FI Tax column — YES charges GST on the line. */
  taxApplicable?: boolean;
  copiedToJob?: boolean;
}

// ── SupplierCodeMap (v1.17) ───────────────────────────────────────────────────
// Persisted reconciliation memory: when a user reconciles a supplier code to an
// internal inventory code, we remember the mapping PER SUPPLIER so future
// imports from the same supplier auto-resolve. Keyed by (supplierId,
// supplierCode). Written ONLY at reconciliation — never on raw import.
export interface SupplierCodeMap {
  supplierId: string;
  supplierCode: string;
  internalCode: string;
}

// ── SupplierPriceBookEntry (v1.18) ────────────────────────────────────────────
// A per-supplier price-book line. This is the FieldInsight "supplier price book"
// made first-class: a supplier code, the supplier's description + cost, and an
// optional link to an internal inventory code (set when reconciled). Created by
// default when a supplier quote is imported, so every supplier code we see
// becomes a managed, per-supplier catalogue entry. `stocked` distinguishes a
// real inventory item from a non-stocked supplier line (default non-stocked).
export interface SupplierPriceBookEntry {
  id: string;
  supplierId: string;
  supplierCode: string;
  description: string;
  unitPrice: number;          // supplier cost, ex GST
  internalCode?: string;      // linked FI inventory code once reconciled
  stocked: boolean;           // false = non-stocked supplier item (default)
  source: 'quote' | 'manual' | 'csv' | 'bill';
  createdAt: string;
}

export interface Bill {
  id: string;
  billNo: string;
  date: string;
  supplierId: string;
  supplierInvoiceNo: string;
  linkedPoId: string | null;
  jobRef: string;
  reference: string;
  status: BillStatus;
  pdfUploaded: boolean;
  processed: boolean;
  subtotalExGst: number;
  totalGst: number;
  totalIncGst: number;
  notes: string;
  // v1.30 — document header fields (Bill page PO-pattern parity)
  template?: string;
  projectId?: string;
  invoiceTerm?: string;
  dueDays?: number;
  dueDate?: string;
  ownerSalesRep?: string;
  division?: string;
  businessEntity?: string;
  purchasesTax?: TaxMode;
  salesTax?: TaxMode;
  createdBy?: string;
  createdAt?: string;
  // v1.47 — straight file attachments (names only; mirrors PO/Quote). The AI
  // import flow is separate (it parses); these are reference documents.
  attachments?: string[];
  // v1.48 — set when an approved bill has been pushed to the accounting product
  // (Xero/MYOB/QuickBooks) for payment. Drives the "sent for payment" status.
  accountingExport?: {
    product: string;
    ref: string;
    at: string;
  };
  // v1.45 — true once this bill's receipts have been synced onto its PO.
  // Prevents double-counting when multiple bills reconcile against one PO
  // (sync is additive); reset to false whenever the bill is re-imported.
  reconciledReceiptsSynced?: boolean;
}

export interface BillLineItem {
  id: number;
  supplierCode: string;
  internalCode: string;
  lineType?: POLineType;
  codeMatch: 'exact' | 'partial' | 'none';
  description: string;
  qty: number;
  unitPrice: number;
  gstTreatment: GstTreatment;
  gstAmount: number;
  lineTotal: number;
  copiedToJob?: boolean;
}

// ── Statement entity ─────────────────────────────────────────────────────────
// A Statement represents an uploaded supplier statement file (PDF or CSV) that
// contains many supplier-invoice lines. The user reconciles each line against
// a FieldInsight PO. A Statement is the parent of many StatementLine entries.

export type StatementStatus = 'Active' | 'In Progress' | 'Completed' | 'Archived';
export type StatementSource = 'pdf' | 'csv-smartbiz' | 'csv-selected-invoices' | 'csv-generic' | 'manual';

export interface StatementLineRecord {
  id: number;
  supplierPoRef: string;   // PO ref as recorded by the supplier
  supplierRef: string;     // supplier's own internal job/docket/invoice ref
  project?: string;
  jobRef?: string;
  description: string;
  amountIncGst: number;
  // Reconciliation outcome (per-line)
  matchedPoId?: string;
  matchStatus?: 'matched' | 'amount-differs' | 'zero-po' | 'no-po';
  applied?: boolean;       // user has applied this line to its PO
  skipped?: boolean;       // user has parked this line
}

export interface Statement {
  id: string;              // e.g. STMT-20260428-001
  filename: string;        // original uploaded filename
  source: StatementSource;
  supplierId?: string;     // single-supplier statements; mixed/CSV may be undefined
  supplierName?: string;   // for display when supplierId not set (e.g. multi-supplier CSV)
  statementDate: string;   // ISO YYYY-MM-DD
  statementRef?: string;
  uploadedAt: string;      // ISO timestamp
  lines: StatementLineRecord[];
  status: StatementStatus;
  notes?: string;
  // For multi-supplier CSV statements, optional rollup
  invoiceCount: number;
  totalIncGst: number;
  // v1.14.2 — when true, the Bill Importer queue is the source of truth for
  // invoiceCount + totalIncGst (the reconciler effect owns the math). When
  // false/undefined, the statement was seeded or imported via a path that
  // owns its own totals and the reconciler must leave them alone.
  queueManaged?: boolean;
}

// ── Quotes (v1.29) ────────────────────────────────────────────────────────────
// Customer-facing Quote entity. Mirrors the FieldInsight Quote authoring screen
// (To / Contact / Project / Details grid / Notes / Jobs & inventory / totals)
// and is the sell-side counterpart to a PurchaseOrder. A Quote is the parent of
// many QuoteLineDetail rows. Statuses follow the FieldInsight quote lifecycle.
export type QuoteStatus =
  | 'Draft'
  | 'Awaiting Acceptance'
  | 'Follow Up'
  | 'Quote Accepted'
  | 'Quote Declined'
  | 'Job Created'
  | 'Job Completed'
  | 'Superseded';

export const QUOTE_STATUSES: QuoteStatus[] = [
  'Draft',
  'Awaiting Acceptance',
  'Follow Up',
  'Quote Accepted',
  'Quote Declined',
  'Job Created',
  'Job Completed',
  'Superseded',
];

export type TaxMode = 'Exclusive' | 'Inclusive';

// A sell-side line item. Unlike a PO line (which tracks cost + received qty),
// a quote line carries cost (purchase) AND a markup → sales price, plus an
// optional per-line discount. Service lines (labour) skip the inventory code.
export interface QuoteLineDetail {
  quoteId: string;
  lineType: POLineType;        // 'product' | 'service'
  inventoryCode: string;
  description: string;
  qty: number;
  purchasePrice: number;       // unit cost, ex GST
  markup: number;              // %
  salesPrice: number;          // unit sell, ex GST (purchase × (1 + markup/100))
  discount: number;            // %
  taxable: boolean;
}

export interface Quote {
  id: string;                   // e.g. 'QUO-0401-1' (number + revision)
  number: string;               // human-friendly base number, e.g. 'QUO-0401'
  revision: number;             // version suffix, e.g. 1
  status: QuoteStatus;
  template: string;             // 'Default Template', 'Defect Quote II', etc.

  // To (customer) — reuses the Site entity as the customer record for the
  // prototype, plus free-text fallbacks for display.
  customerId?: string;
  customerName?: string;
  customerAddress?: string;

  // Contact
  contactName?: string;
  contactEmail?: string;
  contactAddress?: string;

  // Project
  projectId?: string;

  // Details grid
  title?: string;
  invoiceTerm?: string;
  expiryDate?: string;          // YYYY-MM-DD
  validDate?: string;           // YYYY-MM-DD
  acceptedDate?: string;        // YYYY-MM-DD
  purchaseOrderNumber?: string; // customer's PO ref
  quoteDate: string;            // YYYY-MM-DD
  ownerSalesRep?: string;
  probability?: string;         // e.g. '50%'
  expectedStartDate?: string;   // YYYY-MM-DD
  businessEntity?: string;
  division?: string;
  area?: string;
  sent?: boolean;

  // Notes
  revisionNotes?: string;
  internalNotes?: string;
  notesToCustomer?: string;
  exclusionsInclusions?: string;

  // Tax modes (header dropdowns)
  purchasesTax: TaxMode;
  salesTax: TaxMode;

  // Provenance
  createdBy?: string;
  createdAt?: string;           // ISO timestamp

  // File attachments (no parsing — stored for reference / Show in PDF)
  attachments?: string[];

  // Rollups (kept in sync when lines change) — power the list page.
  lines: number;
  exGst: number;                // sell ex GST (subtotal)
  gst: number;
  total: number;                // inc GST
}

// ── Invoices (v1.30) ──────────────────────────────────────────────────────────
// Customer-facing Invoice (AR). Sell-side counterpart to a Quote — raised when
// work is billed to a customer. Mirrors FieldInsight INV authoring screen.
export type InvoiceStatus =
  | 'Draft'
  | 'Awaiting Approval'
  | 'Approved'
  | 'Sent'
  | 'Partially Paid'
  | 'Paid'
  | 'Void';

export const INVOICE_STATUSES: InvoiceStatus[] = [
  'Draft',
  'Awaiting Approval',
  'Approved',
  'Sent',
  'Partially Paid',
  'Paid',
  'Void',
];

export interface InvoiceLineDetail {
  invoiceId: string;
  lineType: POLineType;
  inventoryCode: string;
  description: string;
  qty: number;
  purchasePrice: number;
  markup: number;
  salesPrice: number;
  discount: number;
  taxable: boolean;
}

export interface Invoice {
  id: string;                   // e.g. 'INV-0108'
  status: InvoiceStatus;
  template: string;

  customerId?: string;
  customerName?: string;
  customerAddress?: string;
  contactName?: string;
  contactEmail?: string;
  contactAddress?: string;
  projectId?: string;
  linkedQuoteId?: string;

  title?: string;
  invoiceDate: string;
  invoiceTerm?: string;
  dueDays?: number;
  dueDate?: string;
  paidDate?: string;
  purchaseOrderNumber?: string;
  paymentMethod?: string;
  ownerSalesRep?: string;
  businessEntity?: string;
  division?: string;
  recurrent?: boolean;
  sent?: boolean;

  internalNotes?: string;
  notesToCustomer?: string;
  exclusionsInclusions?: string;

  purchasesTax: TaxMode;
  salesTax: TaxMode;

  createdBy?: string;
  createdAt?: string;

  // File attachments (no parsing)
  attachments?: string[];

  lines: number;
  exGst: number;
  gst: number;
  total: number;
  paidAmount: number;
  owingAmount: number;
}

export interface InvoicePayment {
  id: string;
  invoiceId: string;
  dateCreated: string;
  amountPaid: number;
  paidDate: string;
  paymentMethod: string;
  addedBy: string;
  reference?: string;
}

// ── Jobs (v1.15) ────────────────────────────────────────────────────────────
// Jobs are work items scheduled for a customer, referenced from POs, Bills
// and the Schedule page. Until v1.15 they were a hover-card stub only; now
// they are first-class entities so they can be imported in bulk and edited.
export type JobStatus = 'Scheduled' | 'In Progress' | 'Completed' | 'On Hold' | 'Cancelled';

export interface Job {
  id: string;                // internal id (matches jobRef for simplicity)
  jobRef: string;            // primary key referenced from POs / Bills / Statements
  number: string;            // human-friendly number, e.g. "192"
  title: string;             // short label, e.g. "Ringwood Plaza · Septic Clean"
  customer: string;
  address: string;
  workflow: string;          // e.g. "Septic Clean"
  jobTypes: string;          // free-text classification
  technician: string;
  visitDate: string;         // dd/MM/yyyy (display format used elsewhere)
  startTime: string;         // free-form e.g. "8 AM"
  endTime: string;           // free-form e.g. "11 AM"
  jobAmount: number;
  timesheetHours: number;
  status: JobStatus;
  phone?: string;
  email?: string;
  notes?: string;
  createdAt: string;         // ISO timestamp
  importedFrom?: string;     // optional source filename when CSV-imported
}
